import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useActionModalStateValue } from "@/hooks/actionModal";
import {
    GavelIcon, AlertTriangleIcon, Loader2Icon, ShieldAlertIcon,
    Undo2Icon, TimerOffIcon, TimerIcon, HourglassIcon,
    ListIcon, ChevronLeftIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useBackendApi } from "@/hooks/fetch";
import { HistoryActionModalResp, HistoryActionModalSuccess } from "@shared/historyApiTypes";
import ActionIdsTab from "./ActionIdsTab";
import ActionInfoTab from "./ActionInfoTab";
import ActionModifyTab from "./ActionModifyTab";
import DateTimeCorrected from "@/components/DateTimeCorrected";
import { useAdminPerms } from "@/hooks/auth";

type View = 'overview' | 'ids' | 'revoke';

export default function ActionModal() {
    const { isModalOpen, closeModal, actionRef } = useActionModalStateValue();
    const [view, setView]                         = useState<View>('overview');
    const [currRefreshKey, setCurrRefreshKey]     = useState(0);
    const [modalData, setModalData]               = useState<HistoryActionModalSuccess | undefined>(undefined);
    const [modalError, setModalError]             = useState('');
    const [tsFetch, setTsFetch]                   = useState(0);
    const { hasPerm } = useAdminPerms();

    const historyGetActionApi = useBackendApi<HistoryActionModalResp>({
        method: 'GET',
        path: `/history/action`,
        abortOnUnmount: true,
    });

    const refreshModalData = () => setCurrRefreshKey(k => k + 1);

    useEffect(() => {
        if (!actionRef) return;
        setModalData(undefined);
        setModalError('');
        setView('overview');
        historyGetActionApi({
            queryParams: { id: actionRef },
            success: (resp) => {
                if ('error' in resp) setModalError(resp.error);
                else { setModalData(resp); setTsFetch(Math.round(Date.now() / 1000)); }
            },
            error: (err) => setModalError(err),
        });
    }, [actionRef, currRefreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isModalOpen) setTimeout(() => setView('overview'), 200);
    }, [isModalOpen]);

    const handleOpenClose = (open: boolean) => {
        if (isModalOpen && !open) closeModal();
    };

    const action = modalData?.action;
    const isBan  = action?.type === 'ban';
    const accent = isBan ? '#ef4444' : '#fbbf24';
    const TypeIcon = isBan ? GavelIcon : AlertTriangleIcon;

    // Status badge(s)
    const statusBadges: { label: string; color: string }[] = [];
    if (action) {
        if (action.revocation.timestamp) {
            statusBadges.push({ label: 'Revoked', color: '#94a3b8' });
        } else if (action.type === 'ban') {
            if (action.expiration === false) statusBadges.push({ label: 'Permanent', color: '#ef4444' });
            else if (action.expiration > (modalData?.serverTime ?? 0)) statusBadges.push({ label: 'Active Ban', color: '#fbbf24' });
            else statusBadges.push({ label: 'Expired', color: '#475569' });
        } else if (action.type === 'warn' && !action.acked) {
            statusBadges.push({ label: 'Pending Ack', color: '#fbbf24' });
        }
    }

    const hasRevokePerm = hasPerm(action?.type === 'warn' ? 'players.warn' : 'players.ban');
    const isRevoked     = !!action?.revocation.timestamp;

    return (
        <Dialog open={isModalOpen} onOpenChange={handleOpenClose}>
            <DialogContent className="max-w-3xl p-0 gap-0 flex flex-col bg-[#070b13] border-[#1f2d4d] overflow-hidden text-slate-100 shadow-[0_10px_50px_rgba(0,0,0,0.85),0_0_30px_rgba(31,45,77,0.2)]">

                {/* ── Header ── */}
                <DialogHeader className="px-6 py-4 border-b border-[#1f2d4d] bg-[#0c1322] shrink-0">
                    <DialogTitle className="flex items-center gap-3 text-base font-semibold tracking-wide">
                        {action ? (
                            <>
                                <span className="font-mono text-sm font-bold shrink-0" style={{ color: accent }}>
                                    [{action.id}]
                                </span>
                                <span className="text-xl text-slate-100 font-semibold truncate">
                                    {action.playerName !== false ? action.playerName : <span className="italic text-slate-500">unknown player</span>}
                                </span>
                            </>
                        ) : modalError ? (
                            <span className="flex items-center gap-2 text-destructive text-sm">
                                <ShieldAlertIcon size={16} /> {modalError}
                            </span>
                        ) : (
                            <span className="flex items-center gap-2 text-slate-500 text-sm italic">
                                <Loader2Icon size={14} className="animate-spin" /> Loading...
                            </span>
                        )}
                    </DialogTitle>
                </DialogHeader>

                {/* ── Body ── */}
                <div className="flex flex-col md:flex-row min-h-[460px] max-h-[72vh]">

                    {/* ── LEFT: hero + actions ── */}
                    <div className="w-full md:w-64 shrink-0 flex flex-col items-center p-5 border-r border-[#1f2d4d] bg-[#0a0e1a]/80">
                        {action ? (
                            <>
                                {/* Big type icon */}
                                <div
                                    className="w-20 h-20 rounded-2xl flex items-center justify-center mb-4 border-2"
                                    style={{
                                        background: `${accent}12`,
                                        borderColor: `${accent}30`,
                                        boxShadow: `0 0 30px ${accent}18`,
                                    }}
                                >
                                    <TypeIcon size={36} style={{ color: accent }} />
                                </div>

                                {/* Type label */}
                                <span
                                    className="text-[11px] font-mono font-black uppercase tracking-[0.2em] mb-1"
                                    style={{ color: accent }}
                                >
                                    {action.type}
                                </span>

                                {/* Player name */}
                                <h3 className="text-base font-bold text-center text-slate-200 leading-tight mb-3 px-2">
                                    {action.playerName !== false ? action.playerName : (
                                        <span className="italic text-slate-500 text-sm">unknown player</span>
                                    )}
                                </h3>

                                {/* Status badges */}
                                {statusBadges.length > 0 && (
                                    <div className="flex flex-wrap justify-center gap-1.5 mb-5">
                                        {statusBadges.map(b => (
                                            <span
                                                key={b.label}
                                                className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border font-mono"
                                                style={{
                                                    color: b.color,
                                                    borderColor: `${b.color}30`,
                                                    background: `${b.color}12`,
                                                }}
                                            >
                                                {b.label}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {/* Quick info */}
                                <div className="w-full flex flex-col gap-2 mb-6 px-1">
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-[9px] font-mono font-bold uppercase tracking-widest text-slate-600">Admin</span>
                                        <span className="text-xs font-semibold text-[#00ffd1] font-mono truncate">{action.author}</span>
                                    </div>
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-[9px] font-mono font-bold uppercase tracking-widest text-slate-600">Date</span>
                                        <span className="text-xs text-slate-300">
                                            <DateTimeCorrected
                                                serverTime={modalData!.serverTime}
                                                tsObject={action.timestamp}
                                                tsFetch={tsFetch}
                                            />
                                        </span>
                                    </div>
                                </div>

                                {/* Action buttons at bottom */}
                                <div className="w-full flex flex-col gap-2 mt-auto">
                                    <button
                                        onClick={() => setView(v => v === 'ids' ? 'overview' : 'ids')}
                                        className={cn(
                                            'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold tracking-wide transition-all duration-150 border',
                                            view === 'ids'
                                                ? 'bg-[#00ffd1]/10 border-[#00ffd1]/25 text-[#00ffd1]'
                                                : 'bg-white/[0.02] border-[#1f2d4d] text-slate-400 hover:bg-[#121b2f] hover:text-slate-200 hover:border-slate-600'
                                        )}
                                    >
                                        <ListIcon size={13} /> View Player IDs
                                    </button>

                                    <button
                                        onClick={() => setView(v => v === 'revoke' ? 'overview' : 'revoke')}
                                        disabled={isRevoked || !hasRevokePerm}
                                        className={cn(
                                            'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold tracking-wide transition-all duration-150 border',
                                            view === 'revoke'
                                                ? 'bg-destructive/15 border-destructive/40 text-destructive'
                                                : 'bg-white/[0.02] border-[#1f2d4d] text-slate-400 hover:bg-red-950/40 hover:text-destructive hover:border-destructive/30',
                                            (isRevoked || !hasRevokePerm) && 'opacity-30 cursor-not-allowed pointer-events-none',
                                        )}
                                    >
                                        <Undo2Icon size={13} />
                                        {isRevoked ? 'Already Revoked' : `Revoke ${action.type.charAt(0).toUpperCase() + action.type.slice(1)}`}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="flex items-center justify-center h-full w-full">
                                {modalError
                                    ? <ShieldAlertIcon size={32} className="text-destructive/40" />
                                    : <Loader2Icon size={24} className="animate-spin text-[#00ffd1]/20" />
                                }
                            </div>
                        )}
                    </div>

                    {/* ── RIGHT: content ── */}
                    <ScrollArea className="flex-1 min-w-0">
                        <div className="px-6 py-5">
                            {!action ? (
                                <div className="flex items-center justify-center h-32 text-slate-600">
                                    {modalError
                                        ? <span className="text-destructive text-xs font-mono">Error: {modalError}</span>
                                        : <Loader2Icon size={20} className="animate-spin text-[#00ffd1]/30" />
                                    }
                                </div>
                            ) : (
                                <>
                                    {/* Back header for non-overview views */}
                                    {view !== 'overview' && (
                                        <div className="flex items-center gap-2 pb-4 mb-4 border-b border-[#1f2d4d]">
                                            <button
                                                onClick={() => setView('overview')}
                                                className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-200 transition-colors"
                                            >
                                                <ChevronLeftIcon size={14} /> Back
                                            </button>
                                            <h4 className="text-sm font-bold text-slate-200">
                                                {view === 'ids' ? 'Player Identifiers' : 'Revoke Action'}
                                            </h4>
                                        </div>
                                    )}

                                    {view === 'overview' && (
                                        <ActionInfoTab
                                            action={action}
                                            serverTime={modalData!.serverTime}
                                            tsFetch={tsFetch}
                                        />
                                    )}
                                    {view === 'ids' && (
                                        <ActionIdsTab action={action} />
                                    )}
                                    {view === 'revoke' && (
                                        <ActionModifyTab
                                            action={action}
                                            refreshModalData={refreshModalData}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    </ScrollArea>
                </div>
            </DialogContent>
        </Dialog>
    );
}
